import React, { useEffect, useMemo, useState } from "react"
import eye from "../../assets/images/icons/eye.svg"

import {
  useLazyGetHelpRequestQuery,
  useLazyGetHelpRequestsQuery,
} from "store/api/user/help.api"
import TableContainer from "components/Common/TableContainer"
import { Button, UncontrolledTooltip } from "reactstrap"
import HelpModelView from "modals/helpModelView"
import { getTableSortingHeaderIcon } from "helpers/common"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { useLocation, useNavigate } from "react-router-dom"
import HelpModel from "modals/helpModel"
import { useSelector } from "react-redux"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const HelpTable = () => {
  const [getResults, { isFetching }] = useLazyGetHelpRequestsQuery()
  const [getHelpRes] = useLazyGetHelpRequestQuery()
  const location = useLocation()
  const navigate = useNavigate()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState(null)
  const [helpModel, setHelpModel] = useState(false)
  const { userType } = useSelector((state) => state.user)
  const { currentUserMenuType } = useSelector((state) => state.common)

  //meta title
  document.title = "Help Requests | Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults({
        ...filters,
        usertype: currentUserMenuType,
      }).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const id = queryParams.get("id")
    if (id) {
      getHelpDetails(id)
    }
  }, [location.search])

  const getHelpDetails = async (id) => {
    const { data } = await getHelpRes({ id }).unwrap()
    setDetails(data)
    setOpen(true)
    navigate(location.pathname, { replace: true })
  }

  useEffect(() => {
    getList()
  }, [filters])

  const truncateString = (str, maxLength = 35) => {
    if (str.length <= maxLength) return str
    return str.slice(0, maxLength) + "..."
  }

  const getStatus = (key) => {
    let status = "Active"
    let className = "Active"

    switch (key) {
      case "OPEN":
        status = "Open"
        className = "under_review"
        break

      case "REOPEN":
        status = "Re-open"
        className = "query_raised"
        break

      default:
        status = "Closed"
        className = "approved"
        break
    }

    return (
      <div className={`badge ${className}`}>
        <span></span> {status}
      </div>
    )
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        id: "created_at",
        accessorFn: (row) =>
          row?.created_at ? new Date(row.created_at).getTime() : null,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.formatted_created_at,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Ticket ID"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
      },
      ...(userType === "ADMIN"
        ? [
            {
              header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
              accessorKey: "name",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => {
                const ticketUser = row?.original?.investor_id
                  ? row?.original?.investor
                  : row?.original?.wealthPartner
                return (
                  <DefaultAvatar
                    avatar_url={ticketUser?.avatar_url}
                    email={ticketUser?.email}
                    name={ticketUser?.name}
                  />
                )
              },
            },
          ]
        : []),

      {
        header: "Message",
        accessorKey: "message",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <div>
              <div className="table-user-name">
                {truncateString(row?.original?.message)}
              </div>
            </div>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">{getStatus(row?.original?.status)}</div>
        ),
      },

      {
        id: "action",
        header: "Actions",
        cell: ({ row }) => {
          const viewTooltip = `editTooltip-${row?.original?.id}`

          return (
            <div className="d-flex justify-content-center align-items-center gap-3">
              <>
                <div className="action-view">
                  <div
                    onClick={() => {
                      getHelpDetails(row?.original?.id)
                      setOpen(true)
                    }}
                    id={viewTooltip}
                  >
                    <img src={eye} alt="" />
                    <UncontrolledTooltip placement="top" target={viewTooltip}>
                      View
                    </UncontrolledTooltip>
                  </div>
                </div>
              </>
            </div>
          )
        },
      },
    ]

    return baseColumns
  }, [userType])

  const FilterComponents = () => (
    <>
      {userType !== "ADMIN" && (
        <Button className="btn btn-primary" onClick={() => setHelpModel(true)}>
          <i className="mdi mdi-plus me-1"></i> Create
        </Button>
      )}
    </>
  )

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        FilterComponents={FilterComponents}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />
      {open && details && (
        <HelpModelView
          open={open}
          setOpen={setOpen}
          data={details}
          onClose={() => {
            getList()
            setOpen(false)
          }}
          onSuccess={(id) => getHelpDetails(id)}
        />
      )}

      {helpModel && (
        <HelpModel
          open={helpModel}
          setOpen={setHelpModel}
          onSuccess={() => {
            getList()
            setHelpModel(false)
          }}
        />
      )}
    </>
  )
}

export default HelpTable
